<template>
  <div>
    <v-container class="px-8 py-4" v-if="initialLoadComplete">
      <v-row>
        <v-col cols="8">
          <breadcrumbs></breadcrumbs>
          <h1>{{ this.organizationId ? "Edit" : "Create" }} Organization</h1>
        </v-col>
      </v-row>
      <v-divider class="mb-5"></v-divider>
      <v-row :justify="`center`">
        <v-col cols="12" xs="12" md="10" lg="8" xl="6">
          <h2 class="title mb-5">General</h2>
          <v-form @submit.prevent="onSubmit" v-model="valid" data-cy="org-form">
            <v-autocomplete
              :items="salesforceList"
              @change="updateCreateForm"
              item-text="name"
              item-value="salesforceId"
              label="Salesforce Account"
              data-cy="salesforce-autocomplete"
              outlined
              v-model="organization.salesforceId"
            ></v-autocomplete>
            <div v-if="organization.salesforceId || this.organizationId">
              <v-text-field
                :rules="organizationNameRules"
                label="Name of Organization"
                outlined
                required
                v-model="organization.name"
                data-cy="organization-name"
              ></v-text-field>
              <v-text-field
                :rules="organizationAbbreviationRules"
                @keyup="
                  organization.abbreviation = organization.abbreviation.toUpperCase()
                "
                label="Abbreviation"
                outlined
                required
                v-model="organization.abbreviation"
                data-cy="organization-abbreviation"
                maxlength="10"
              ></v-text-field>
              <v-autocomplete
                label="Care Setting"
                outlined
                :items="careSetting.enum"
                :rules="careSettingRules"
                v-model="organization.careSetting"
                data-cy="careSetting"
              ></v-autocomplete>
              <v-text-field
                :rules="organizationDomainRules"
                hint="e.g., example.com"
                label="Email Domain"
                outlined
                persistent-hint
                required
                v-model="organization.domain"
                data-cy="domain"
              ></v-text-field>
            </div>

            <div v-if="organizationId">
              <v-divider class="my-5"></v-divider>
              <h2 class="title mb-5">Workflow Settings</h2>
              <v-checkbox
                data-cy="contractSigned"
                :false-value="0"
                :true-value="1"
                label="Contract Signed"
                v-model="organization.contractSigned"
              ></v-checkbox>
              <label class="och-label">Business Development Lead</label>
              <v-autocomplete
                data-cy="bizdev-lead"
                :item-text="userFullName"
                :items="ochinUserList"
                :search-input.sync="searchBusinessDevelopmentLead"
                clearable
                dense
                item-value="username"
                outlined
                v-model="organization.businessDevelopmentLead"
              ></v-autocomplete>
              <label class="och-label" v-if="organization.contractSigned"
                >Project Manager</label
              >
              <v-autocomplete
                data-cy="project-manager"
                :item-text="userFullName"
                :items="ochinUserList"
                :search-input.sync="searchOchinProjectManager"
                clearable
                dense
                item-value="username"
                outlined
                v-if="organization.contractSigned"
                v-model="organization.ochinProjectManager"
              ></v-autocomplete>
              <label class="och-label">NMQ Status</label>
              <v-autocomplete
                data-cy="status"
                :item-text="nmqFormattedListItem"
                :items="nmqStatusList"
                :search-input.sync="searchStatusList"
                dense
                item-value="name"
                outlined
                v-model="organization.status"
              ></v-autocomplete>

              <label class="och-label">Comments for Biz Dev - PM Handoff</label>
              <v-textarea
                data-cy="comments-biz-dev"
                :rules="commentRules"
                auto-grow
                clearable
                counter="250"
                dense
                maxlength="250"
                outlined
                required
                v-model="organization.comments"
              ></v-textarea>
            </div>
            <div class="text-right mt-5">
              <v-btn
                :to="{ name: 'ochinOrganizationList' }"
                class="mr-3"
                color="grey darken-2"
                outlined
                >Cancel</v-btn
              >
              <v-btn
                @click="save"
                :disabled="!valid"
                class="elevation-0"
                color="primary"
                data-cy="save-btn"
                >Save</v-btn
              >
            </div>
          </v-form>
        </v-col>
      </v-row>
    </v-container>
    <generic-loader v-else></generic-loader>
  </div>
</template>
<script>
import { cloneDeep, pick } from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import Breadcrumbs from "@/views/Breadcrumbs";
import GenericLoader from "@/components/GenericLoader";

export default {
  components: {
    Breadcrumbs,
    GenericLoader,
  },
  computed: {
    organizationId() {
      return parseInt(this.$route.params.organizationId, 10);
    },
    attributeList() {
      return this.getEntityTypeByName({ entityName: "organization" });
    },
    careSetting() {
      return this.attributeList.find(({ name }) => name === "careSetting");
    },
    ...mapGetters({
      getByUsername: "coreTeam/getByUsername",
      getOrganizationById: "organization/getById",
      getSalesforceAccount: "salesforce/getById",
      getSubsection: "nmq/getSubsection",
      nmqStatusList: "organizationStatus/statusNameAndLabelList",
      getLabelShortByStatus: "organizationStatus/getLabelShortByStatus",
      getEntityTypeByName: "attribute/filter",
    }),
    ...mapState({
      list: state => state.organization.listAdmin,
      ochinUserList: state => state.coreTeam.ochinUserList,
      salesforceList: state => state.salesforce.list,
    }),
  },
  async created() {
    try {
      await Promise.all([
        this.fetchOrganizationListAdmin(),
        this.fetchOchinUserList(),
        this.listSalesforceAccounts(),
        this.getAttributeList({ reload: false }),
      ]);
    } catch (e) {
      console.error(e);
      this.$notifyError({
        message:
          "Could not load prerequisite data. Please reload page to try again.",
      });
      return;
    }

    if (this.organizationId) {
      this.organization = cloneDeep(
        this.list.find(
          organization => organization.organizationId === this.organizationId
        )
      );
    }
    this.initialLoadComplete = true;
  },
  data() {
    return {
      commentRules: [
        comment =>
          !comment ||
          comment.length < 250 ||
          "Comment cannot be more than 250 characters.",
      ],
      careSettingRules: [name => !!name || "Care setting is required"],
      editAttributeList: [
        "abbreviation",
        "businessDevelopmentLead",
        "careSetting",
        "comments",
        "contractSigned",
        "domain",
        "name",
        "ochinProjectManager",
        "salesforceId",
        "status",
      ],
      initialLoadComplete: false,
      organization: {},
      organizationAbbreviationRules: [
        v => !!v || "Abbreviation is required",
        v =>
          !this.abbreviationConflicts(v) ||
          "Another organization already uses this abbreviation. Please choose another one.",
      ],
      organizationNameRules: [v => !!v || "Organization Name is required"],
      organizationDomainRules: [
        v => !!v || "Email Domain is required",
        v =>
          !!`${v}`
            .trim()
            .match(
              /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i
            ) || "Invalid domain format",
      ],
      searchStatusList: null,
      searchBusinessDevelopmentLead: null,
      searchOchinProjectManager: null,
      valid: false,
    };
  },
  methods: {
    abbreviationConflicts(abbreviation) {
      const { list: organizationList, organizationId } = this;

      if (!abbreviation) return true;

      const conflict = organizationList.find(
        org =>
          `${org.abbreviation}`.trim().toUpperCase() ===
          `${abbreviation}`.trim().toUpperCase()
      );

      if (conflict) {
        if (organizationId && conflict.organizationId === organizationId) {
          return false;
        }

        return true;
      }

      return false;
    },
    async createOrganization() {
      try {
        this.organization.status = "nmqOpen";
        await this.create(this.organization);
      } catch (e) {
        if (e.response.status === 409) {
          console.error(e);
          this.$notifyWarn({
            message: e.response.data.message,
            timeout: false,
          });
        } else {
          console.error(e);
          this.$notifyError({
            message: "Error creating organization",
          });
          return;
        }
      }
      this.fetchOrganizationListAdmin();
      this.$router.push({ name: "ochinOrganizationList" });
    },

    nmqFormattedListItem(item) {
      return `${item.labelShort} (${item.label})`;
    },

    async save() {
      if (!this.organizationId) {
        return this.createOrganization();
      }
      const { editAttributeList, organization, organizationId } = this;

      const organizationToSubmit = Object.assign(
        {},
        pick(organization, editAttributeList),
        { organizationId }
      );

      for (const editAttribute of editAttributeList) {
        if (organizationToSubmit[editAttribute] === undefined) {
          organizationToSubmit[editAttribute] = null;
        }
      }

      try {
        await this.update([
          organizationId,
          organizationToSubmit,
          this.$notifySuccess({ message: "Organization saved successfully" }),
        ]);
      } catch (e) {
        console.error(e);
        this.$notifyError({ message: "Could not save organization" });
      }

      await this.fetchOrganizationListAdmin();
      this.$router.push({ name: "ochinOrganizationList" });
    },
    updateCreateForm(val) {
      if (this.organizationId) return;
      const salesforceRecord = this.salesforceList.find(
        item => item.salesforceId === val
      );
      this.organization.name = salesforceRecord.name;
    },
    userFullName(item) {
      return `${item.firstName} ${item.lastName} (${item.email})`;
    },
    ...mapActions({
      create: "organizationAdmin/create",
      fetchOchinUserList: "coreTeam/ochinUserList",
      fetchOrganizationListAdmin: "organization/listAdmin",
      listSalesforceAccounts: "salesforce/list",
      update: "organizationAdmin/update",
      getAttributeList: "attribute/list",
    }),
  },
};
</script>
